// Messaging query layer. Pairs with `lib/views/messaging.ts` (server fetchers
// used during SSR prefetch) and `lib/mutations/messaging.ts`.
//
// No 'use client' directive: the hooks are React hooks called from client
// components, but server code (pages, layouts) imports the keys to wire up
// prefetches.

import {
  keepPreviousData,
  queryOptions,
  infiniteQueryOptions,
  useQuery,
  useInfiniteQuery,
} from '@tanstack/react-query';
import type { ConversationId, UserId } from '@leanin/shared/ids';
import type { components } from '@leanin/api-client';
import { getApiClient } from '../api-client';
import { throwApiError } from '../api-error';

// Directory search result shape for the messages "new message" / "add to
// group" pickers. Same shape the previous `searchDirectoryAction` returned.
export type DirectoryMemberRow = {
  id: string;
  name: string;
  subtitle: string;
  photo?: string;
};

export type ConversationSummary = components['schemas']['ConversationSummary'];
export type ConversationsPage = components['schemas']['ConversationsPage'];
export type ConversationDetail = components['schemas']['ConversationDetail'];
export type ConversationDetailEnvelope =
  components['schemas']['ConversationDetailEnvelope'];
export type ConversationScopeCounts =
  components['schemas']['ConversationScopeCounts'];
export type MessageView = components['schemas']['MessageView'];
export type MessagesPage = components['schemas']['MessagesPage'];
export type BlocksPage = components['schemas']['BlocksPage'];

export type ListConversationsInput = {
  pageSize?: number;
  kind?: 'dm' | 'group';
  q?: string;
  unreadOnly?: boolean;
};

// New-message thread polling cadence. 5s matches the existing SSE-replacement
// budget — fast enough that a reply lands in roughly one breath, slow enough
// that an idle thread doesn't hammer the API. `staleTime: 0` on the messages
// query keeps `refetchInterval` honest.
export const MESSAGES_POLL_INTERVAL_MS = 5_000;

// Thread page size. A module constant (decision 5b) so it never segments the
// cursor-paginated messages cache: it is written inline in the `messages` leaf
// `queryFn`, absent from the key. Including it would split the cache and break
// prefix matching for cross-variant operations like optimistic message append.
const MESSAGES_PAGE_SIZE = 50;

// Placeholder id for the nullable-id hooks (`useConversation`,
// `useInfiniteMessages`): the leaf factory needs a `ConversationId` to build a
// branded queryOptions, but the read is gated off via `enabled: false` when the
// real id is null, so the placeholder key is never fetched against.
const EMPTY_CONVERSATION_ID = '' as ConversationId;

// ---- Client fetchers ----

export async function fetchConversations(
  params: ListConversationsInput & { cursor?: string },
): Promise<ConversationsPage> {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/conversations', {
    params: {
      query: {
        pageSize: params.pageSize,
        cursor: params.cursor,
        kind: params.kind,
        q: params.q,
        ...(params.unreadOnly !== undefined
          ? { unread: params.unreadOnly ? 'true' : 'false' }
          : {}),
      },
    },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchConversation(
  id: ConversationId,
): Promise<ConversationDetailEnvelope> {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/conversations/{id}', {
    params: { path: { id } },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchConversationMessages(params: {
  conversationId: ConversationId;
  cursor?: string;
  pageSize: number;
}): Promise<MessagesPage> {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/conversations/{id}/messages',
    {
      params: {
        path: { id: params.conversationId },
        query: {
          pageSize: params.pageSize,
          cursor: params.cursor,
        },
      },
    },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchConversationScopeCounts(
  q: string | null,
): Promise<ConversationScopeCounts> {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/conversations/scope-counts',
    {
      params: {
        query: {
          ...(q !== null ? { q } : {}),
        },
      },
    },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchBlocks(input: {
  cursor?: string;
  pageSize?: number;
}): Promise<BlocksPage> {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/blocks', {
    params: {
      query: {
        pageSize: input.pageSize,
        cursor: input.cursor,
      },
    },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchDirectory(
  query: string,
): Promise<readonly DirectoryMemberRow[]> {
  const client = getApiClient();
  const { data } = await client.GET('/api/v1/members', {
    params: {
      query: {
        pageSize: 20,
        sort: 'connection',
        ...(query.length > 0 ? { q: query } : {}),
      },
    },
  });
  if (data === undefined) return [];
  return data.items.map((m) => {
    const subtitle = [m.jobTitle, m.company]
      .filter((s): s is string => typeof s === 'string' && s.length > 0)
      .join(' · ');
    const out: DirectoryMemberRow = {
      id: m.id,
      name: [m.firstName, m.lastName]
        .filter((s): s is string => typeof s === 'string' && s.length > 0)
        .join(' '),
      subtitle,
    };
    if (m.photoUrl !== undefined && m.photoUrl !== null) {
      out.photo = m.photoUrl;
    }
    return out;
  });
}

// ---- Query keys + leaves ----
//
// Inner nodes (`all`, `conversations`) stay bare `as const` arrays — they are
// invalidation prefixes. Leaves return queryOptions / infiniteQueryOptions and
// normalize their raw args ONCE so the cache key and the fetch call derive from
// the same object (decision 3).

export const messagingKeys = {
  all: ['messaging'] as const,
  conversations: () => [...messagingKeys.all, 'conversations'] as const,

  conversationsList: (raw: ListConversationsInput) => {
    const args: ListConversationsInput = {
      ...(raw.pageSize !== undefined ? { pageSize: raw.pageSize } : {}),
      ...(raw.kind !== undefined ? { kind: raw.kind } : {}),
      ...(raw.q !== undefined ? { q: raw.q.trim() } : {}),
      ...(raw.unreadOnly !== undefined ? { unreadOnly: raw.unreadOnly } : {}),
    };
    return queryOptions({
      queryKey: [...messagingKeys.conversations(), 'list', args] as const,
      queryFn: () => fetchConversations(args),
    });
  },

  conversationsInfinite: (raw: ListConversationsInput) => {
    const args: ListConversationsInput = {
      ...(raw.pageSize !== undefined ? { pageSize: raw.pageSize } : {}),
      ...(raw.kind !== undefined ? { kind: raw.kind } : {}),
      ...(raw.q !== undefined ? { q: raw.q.trim() } : {}),
      ...(raw.unreadOnly !== undefined ? { unreadOnly: raw.unreadOnly } : {}),
    };
    return infiniteQueryOptions({
      queryKey: [...messagingKeys.conversations(), 'list', args] as const,
      queryFn: ({ pageParam }) =>
        fetchConversations({ ...args, cursor: pageParam ?? undefined }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      placeholderData: keepPreviousData,
    });
  },

  conversation: (id: ConversationId) =>
    queryOptions({
      queryKey: [...messagingKeys.all, 'conversation', id] as const,
      queryFn: () => fetchConversation(id),
    }),

  conversationScopeCounts: (rawQ?: string) => {
    const q = rawQ ?? null;
    return queryOptions({
      queryKey: [...messagingKeys.all, 'conversation-scope-counts', q] as const,
      queryFn: () => fetchConversationScopeCounts(q),
    });
  },

  // PageSize is a module constant in the queryFn, NOT in the key (decision 5b)
  // — it's a fetch parameter, not part of the conversation's message-thread
  // identity. Cursor is injected via pageParam, never in the key. Keeping the
  // key shape `[...all,'messages',conversationId]` is what lets the optimistic
  // message append in mutations/messaging.ts prefix-match every page variant.
  messages: (conversationId: ConversationId) =>
    infiniteQueryOptions({
      queryKey: [...messagingKeys.all, 'messages', conversationId] as const,
      queryFn: ({ pageParam }) =>
        fetchConversationMessages({
          conversationId,
          pageSize: MESSAGES_PAGE_SIZE,
          cursor: pageParam ?? undefined,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      staleTime: 0,
      refetchInterval: MESSAGES_POLL_INTERVAL_MS,
    }),

  directory: (rawQuery: string) => {
    const q = rawQuery.trim();
    return queryOptions({
      queryKey: [...messagingKeys.all, 'directory', q] as const,
      queryFn: () => fetchDirectory(q),
      staleTime: 30 * 1000,
      placeholderData: keepPreviousData,
    });
  },

  blocks: (raw: { cursor?: string; pageSize?: number } = {}) => {
    const args = {
      ...(raw.pageSize !== undefined ? { pageSize: raw.pageSize } : {}),
    };
    return queryOptions({
      queryKey: [...messagingKeys.all, 'blocks', args] as const,
      queryFn: () => fetchBlocks(args),
    });
  },
};

// ---- Hooks ----

export function useConversations(input: ListConversationsInput) {
  return useQuery(messagingKeys.conversationsList(input));
}

export function useInfiniteConversations(input: ListConversationsInput) {
  return useInfiniteQuery(messagingKeys.conversationsInfinite(input));
}

export function useConversation(id: ConversationId | null) {
  // Guard at the hook (decision 3 permits this for nullable ids). The leaf is
  // the single source of key + fetch; `enabled: id !== null` gates the read so
  // the queryFn never runs with the placeholder id. Building the leaf with a
  // placeholder when null (rather than a fallback union key) keeps the branded
  // queryOptions type intact — a `?? guardKey` union breaks TanStack inference.
  return useQuery({
    ...messagingKeys.conversation(id ?? EMPTY_CONVERSATION_ID),
    enabled: id !== null,
  });
}

// Paginated messages for a thread. Polling replaces the previous SSE stream:
// `refetchInterval: MESSAGES_POLL_INTERVAL_MS` re-fetches the first page on a
// 5s cadence so newly-delivered peer messages appear without per-thread SSE
// wiring. The polling pauses automatically when the tab is hidden
// (`refetchIntervalInBackground` is left at its default `false`).
export function useInfiniteMessages(conversationId: ConversationId | null) {
  // Guard at the hook (decision 3 permits this for nullable ids). The leaf is
  // the single source of the load-bearing key (no pageSize, decision 5b), the
  // cursor wiring, and the `staleTime: 0` + `refetchInterval` polling options
  // (decision 7). `enabled: id !== null` gates the read so the queryFn never
  // runs with the placeholder id; polling is suppressed in the same case.
  return useInfiniteQuery({
    ...messagingKeys.messages(conversationId ?? EMPTY_CONVERSATION_ID),
    enabled: conversationId !== null,
    refetchInterval: conversationId !== null ? MESSAGES_POLL_INTERVAL_MS : false,
  });
}

export function useConversationScopeCounts(q?: string) {
  return useQuery(messagingKeys.conversationScopeCounts(q));
}

// Directory search for the messages pickers. The /members endpoint with
// `sort: 'connection'` ranks people the viewer follows or who follow them
// ahead of strangers, and the server applies the same reachability +
// not-blocked filter the DM gate does — so every row returned here is safe
// to start a 1:1 with.
export function useDirectory(query: string) {
  return useQuery(messagingKeys.directory(query));
}

export function useBlocks(input: { cursor?: string; pageSize?: number } = {}) {
  return useQuery(messagingKeys.blocks(input));
}

// Re-export the messaging-domain id types so consumers can import either ids
// or query helpers from a single place when convenient.
export type { ConversationId, UserId };
